import { EPITAPH_FONTS } from "../lib/constants";
import { lifeSpan } from "../lib/format";

export const FACE_W = 600;
export const FACE_H = 820;

const STYLE = {
  classic: { ink: "#1a130b", soft: "#21180f", frame: "#7a6648", mat: "#cfc5b2", hi: "rgba(255,255,255,0.3)" },
  modern: { ink: "#f3d892", soft: "#e6c77e", frame: "#d4ad62", mat: "#2a2b30", hi: "rgba(0,0,0,0.85)" },
  natural: { ink: "#fbf1dc", soft: "#f1e4c8", frame: "#e3d2ad", mat: "#7f796c", hi: "rgba(0,0,0,0.75)" },
};
const SCALE = 2;

function engrave(ctx, st, text, x, y) {
  ctx.save();
  ctx.fillStyle = st.hi;
  ctx.fillText(text, x + 1.2, y + 1.4);
  ctx.restore();
  ctx.fillText(text, x, y);
}

const cache = new Map();

export function loadImage(url) {
  if (!url) return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
  cache.set(url, p);
  return p;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCover(ctx, img, x, y, w, h) {
  const s = Math.max(w / img.width, h / img.height);
  const iw = img.width * s;
  const ih = img.height * s;
  ctx.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
}

function drawPaw(ctx, cx, cy, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(cx, cy + 22, 34, 28, 0, 0, Math.PI * 2);
  ctx.fill();
  [[-38, -16], [-14, -40], [14, -40], [38, -16]].forEach(([dx, dy]) => {
    ctx.beginPath();
    ctx.ellipse(cx + dx, cy + dy, 13, 17, 0, 0, Math.PI * 2);
    ctx.fill();
  });
}

function fitFont(ctx, text, maxW, start, min, family) {
  let size = start;
  ctx.font = family(size);
  while (size > min && ctx.measureText(text).width > maxW) {
    size -= 2;
    ctx.font = family(size);
  }
  return size;
}

function wrap(ctx, text, maxW) {
  const words = text.split(/(\s+)/).filter((w) => w.trim());
  const lines = [];
  let line = "";
  const pushChars = (word) => {
    for (const ch of word) {
      if (ctx.measureText(line + ch).width > maxW && line) {
        lines.push(line);
        line = ch;
      } else line += ch;
    }
  };
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxW) line = test;
    else if (ctx.measureText(w).width > maxW) {
      if (line) line += " ";
      pushChars(w);
    } else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

const serif = (weight, italic = false) => (size) =>
  `${italic ? "italic " : ""}${weight} ${size}px "Playfair Display", "Noto Serif", "Noto Naskh Arabic", "Noto Sans Hebrew", "Noto Sans SC", "Noto Sans JP", "Noto Sans KR", "Noto Sans Devanagari", "Noto Sans Thai", serif`;

export async function drawFace(canvas, m, model) {
  const ef = EPITAPH_FONTS[m.epitaph_font] || EPITAPH_FONTS.classic;
  try {
    await document.fonts?.load(`${ef.italic ? "italic " : ""}${ef.weight} 40px ${ef.css}`);
    await document.fonts?.ready;
  } catch {}
  const st = STYLE[model] || STYLE.classic;
  canvas.width = FACE_W * SCALE;
  canvas.height = FACE_H * SCALE;
  const ctx = canvas.getContext("2d");
  ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
  ctx.clearRect(0, 0, FACE_W, FACE_H);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  const img = await loadImage(m.photoUrl);
  const cx = FACE_W / 2;

  ctx.save();
  if (model === "modern") {
    roundRect(ctx, cx - 140, 64, 280, 300, 22);
  } else {
    ctx.beginPath();
    ctx.ellipse(cx, 220, 132, 150, 0, 0, Math.PI * 2);
  }
  ctx.clip();
  ctx.fillStyle = st.mat;
  ctx.fillRect(0, 0, FACE_W, FACE_H);
  if (img) drawCover(ctx, img, cx - 150, 60, 300, 320);
  else drawPaw(ctx, cx, 215, st.soft);
  ctx.restore();

  ctx.lineWidth = model === "modern" ? 5 : 7;
  ctx.strokeStyle = st.frame;
  if (model === "modern") {
    roundRect(ctx, cx - 140, 64, 280, 300, 22);
  } else {
    ctx.beginPath();
    ctx.ellipse(cx, 220, 132, 150, 0, 0, Math.PI * 2);
  }
  ctx.stroke();

  ctx.fillStyle = st.ink;
  const narrow = model === "natural" ? 0.8 : 1;
  const nameSize = fitFont(ctx, m.name || "", 540 * narrow, 96, 40, serif(700));
  engrave(ctx, st, m.name || "", cx, 470);
  let y = 470 + Math.max(36, nameSize * 0.5);
  if (m.surname) {
    fitFont(ctx, m.surname, 520 * narrow, 46, 26, serif(600));
    ctx.fillStyle = st.soft;
    engrave(ctx, st, m.surname, cx, y + 12);
    y += 54;
  }
  const span = lifeSpan(m);
  if (span) {
    fitFont(ctx, span, 540 * narrow, 38, 22, serif(600));
    ctx.fillStyle = st.soft;
    engrave(ctx, st, span, cx, y + 14);
    y += 48;
  }
  ctx.strokeStyle = st.frame;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx - 46, y + 8);
  ctx.lineTo(cx + 46, y + 8);
  ctx.stroke();
  ctx.fillStyle = st.frame;
  ctx.beginPath();
  ctx.arc(cx, y + 8, 4, 0, Math.PI * 2);
  ctx.fill();
  y += 44;

  if (m.epitaph) {
    let size = Math.round(38 * ef.scale);
    let lines = [];
    while (size >= 22) {
      ctx.font = `${ef.italic ? "italic " : ""}${ef.weight} ${size}px ${ef.css}, "Noto Serif", serif`;
      lines = wrap(ctx, m.epitaph, 530 * narrow);
      if (lines.length * size * 1.32 <= FACE_H - y - 24) break;
      size -= 2;
    }
    ctx.fillStyle = st.ink;
    lines.slice(0, 6).forEach((line, i) => engrave(ctx, st, line, cx, y + i * size * 1.28 + size * 0.6));
  }
}
